import { serializeError } from 'serialize-error';
import moment from 'moment';
import pluralize from 'pluralize';
import typeCheck from 'common/util/typeCheck';
import capitalize from 'common/util/capitalize';
import lodashFilter from 'lodash/filter';
import lodashSortBy from 'lodash/sortBy';
import log from 'client/lib/log';
import { LEAD_ACTIVITY_ASSIGNED } from 'common/leadConstants';
import { PAYMENT_ACCOUNT_BANK_ACCOUNT, PAYMENT_ACCOUNT_CARD } from 'common/paymentAccountConstants';
import { getListingFieldsForDisplay } from 'common/listingHelpers';
import { getApartmentOfficeHoursByDate } from 'common/tourHelpers';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

export function numberWithCommas(number) {
  if (!number) {
    return '0';
  }

  return `${number}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function numberFixedTwoDecimals(number) {
  if (!number) {
    return 0;
  }
  // If the number is an integer or string return it.
  if (Number.isInteger(number) || typeof number === 'string') {
    return number;
  }

  const numberFloatString = number.toString();
  const numberFixedDecimals = numberFloatString.slice(0, numberFloatString.indexOf('.') + 3);

  return numberFixedDecimals;
}

export function squareFeetDisplayString({ minSquareFeet, maxSquareFeet }) {
  return !minSquareFeet && maxSquareFeet
    ? `${numberWithCommas(maxSquareFeet)} sqft or less`
    : minSquareFeet && !maxSquareFeet
    ? `${numberWithCommas(minSquareFeet)} sqft or greater`
    : minSquareFeet && maxSquareFeet
    ? `${numberWithCommas(minSquareFeet)} - ${numberWithCommas(maxSquareFeet)} sqft`
    : '--';
}

export function bedBathsDisplayString(params) {
  try {
    typeCheck('params::NonEmptyObject', params);
    const bedrooms = params.bedrooms || 0;
    const bathrooms = params.bathrooms || 0;
    typeCheck('bedrooms::Number|Array', bedrooms);
    typeCheck('bathrooms::Maybe Number', bathrooms);
    const bedroomsPart = bedsDisplayString({ bedrooms });

    return `${bedroomsPart} / ${bathrooms} bath`;
  } catch (bedBathsDisplayStringError) {
    const error = new Error(
      'Error making the bedBathsDisplayString: ' + bedBathsDisplayStringError.message,
    );
    error.name = 'SrBedBathsDisplayStringError';
    error.inner = serializeError(bedBathsDisplayStringError);
    error.details = params;
    log.error(error);
    return '';
  }
}

export function bathsDisplayString(params) {
  try {
    typeCheck('params::NonEmptyObject', params);
    const bathrooms = params.bathrooms || 0;
    typeCheck('bathrooms::Maybe Number', bathrooms);

    return `${bathrooms} bath`;
  } catch (bathsDisplayStringError) {
    const error = new Error(
      'Error making the bathsDisplayString: ' + bathsDisplayStringError.message,
    );
    error.name = 'SrBathsDisplayStringError';
    error.inner = serializeError(bathsDisplayStringError);
    error.details = params;
    log.error(error);
    return '';
  }
}

export function bedsDisplayString({ bedrooms }) {
  try {
    typeCheck('bedrooms::Maybe Number|Array', bedrooms);
    return Array.isArray(bedrooms)
      ? bedrooms.length === 1 && bedrooms[0] === 0
        ? 'Studio'
        : `${bedrooms.join(', ')} bed`
      : !bedrooms
      ? 'Studio'
      : `${bedrooms} bed`;
  } catch (bedsDisplayStringError) {
    const error = new Error(
      `Error making the bedsDisplayString: ${bedsDisplayStringError.message}`,
    );
    error.name = 'SrBedsDisplayStringError';
    error.inner = serializeError(bedsDisplayStringError);
    log.error(error);
    return '';
  }
}

export function bedsDisplayMultipleString({ bedrooms }) {
  try {
    typeCheck('bedrooms::Number|Array', bedrooms);
    return Array.isArray(bedrooms)
      ? bedrooms[0] === 0 && bedrooms.length === 1
        ? 'Studio'
        : bedrooms[0] === 0 && bedrooms.length > 1
        ? 'Studio+'
        : bedrooms[0] > 0 && bedrooms.length > 1
        ? `${bedrooms[0]}+`
        : `${bedrooms[0]}`
      : bedrooms === 0
      ? 'Studio'
      : `${bedrooms}`;
  } catch (bedsDisplayStringError) {
    const error = new Error(
      `Error making the bedsDisplayString: ${bedsDisplayStringError.message}`,
    );
    error.name = 'SrBedsDisplayStringError';
    error.inner = serializeError(bedsDisplayStringError);
    log.error(error);
    return '';
  }
}

export function formatUnitAmenities(amenitiesArray) {
  typeCheck('amenitiesArray::Array', amenitiesArray);
  const formattedAmenities = [];
  const amenityDict = {
    'washer/dryer - in unit': 'W/D - In Unit',
    'washer/dryer - connections': 'W/D - Connections',
    'wood flooring': 'Wood Flooring',
    pool: 'Pool',
  };
  for (let i = 0; i < amenitiesArray.length; i += 1) {
    formattedAmenities.push(amenityDict[amenitiesArray[i]]);
  }
  return formattedAmenities.join(', ');
}

export function formatPetString(petArray) {
  let petStr = 'No Pets';
  if (petArray.includes('dog') && petArray.includes('cat')) {
    petStr = 'Dogs, Cats';
  } else if (petArray.includes('dog')) {
    petStr = 'Dogs';
  } else if (petArray.includes('cat')) {
    petStr = 'Cats';
  }
  return petStr;
}

export function formatListingPriceCompact({ listing, currentUser }) {
  const listingFieldsForDisplay = getListingFieldsForDisplay({
    listing: listing,
    searchFilters: currentUser.searchFilters,
  });

  const price = isNaN(listingFieldsForDisplay.price)
    ? parseInt(listingFieldsForDisplay.price.split('-')[0].trim(), 10)
    : listingFieldsForDisplay.price;

  return formatPriceCompact(price);
}

export function formatPriceCompact(price) {
  if (price < 1000) {
    return `$${price}`;
  }

  const decimals = parseInt(price / 1000, 10);
  const cents = parseInt((price % 1000) / 100, 10);

  return `$${decimals}.${cents}k`;
}

export function formatWithTwoDecimals(price) {
  let priceString = String(price);
  if (priceString.includes('.')) {
    const priceStringParts = priceString.split('.');
    if (priceStringParts[priceStringParts.length - 1].length === 1) {
      priceString = `${priceString}0`;
    } else if (priceStringParts[priceStringParts.length - 1].length > 2) {
      priceString = priceStringParts[0] + '.' + priceStringParts[1].slice(0, 2);
    }
  }
  return priceString;
}

export function formatPrice(price) {
  if (typeof price !== 'string' && typeof price !== 'number') {
    return '$0';
  }

  // Support price values that were already formatted.
  if (typeof price === 'string') {
    price = price.replace(/[^.\d]/g, '');
    if (isNaN(price)) {
      return '$0';
    }
  }

  price = roundTwoDecimals(parseFloat(price));
  return `$${formatWithTwoDecimals(numberWithCommas(price))}`;
}

export function formatListingAddress(listing, options = {}) {
  typeCheck('listing::NonEmptyObject', listing);

  const addressStreetDisplay = options.hideApartmentNumber
    ? [
        listing.addressStreetNumber || '',
        listing.addressStreetName || '',
        listing.addressStreetSuffix || '',
      ]
        .join(' ')
        .trim()
    : listing.addressStreetDisplay.trim();

  return [addressStreetDisplay, listing.addressCity, listing.addressStateCode].join(', ').trim();
}

export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== 'string' || phoneNumber.length !== 10) {
    return '';
  }
  const matchedPhoneNumber = `${phoneNumber}`.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!matchedPhoneNumber) {
    return '';
  }
  return `(${matchedPhoneNumber[1]}) ${matchedPhoneNumber[2]}-${matchedPhoneNumber[3]}`;
}

export function formatApartmentOfficeHoursByDate({ listing, date }) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('date::Date', date);

  const momentInstance = momentWithAustinTimezone(date);
  const apartmentOfficeHours = getApartmentOfficeHoursByDate({ listing, date: momentInstance })
    .map((officeHours) => {
      return officeHours.map((officeHour) => officeHour.format('h:mmA')).join(' - ');
    })
    .join(' / ');

  return `${apartmentOfficeHours} (${momentInstance.format('dddd')})`;
}

export function formatPriceRange({ minRentPrice, maxRentPrice }) {
  return `$${numberWithCommas(minRentPrice)}-${numberWithCommas(maxRentPrice)}`;
}

export function roundTwoDecimals(number) {
  return Math.round(number * 100) / 100;
}

export function getFormattedFromNowDate({ date, compact }) {
  const pastDate = momentWithAustinTimezone(date);
  const nowDate = momentWithAustinTimezone();
  const dateDifference = Math.floor((nowDate.unix() - pastDate.unix()) / ONE_DAY_IN_SECONDS);

  if (dateDifference === 0) {
    const hourDifference = nowDate.diff(pastDate, 'hours');
    if (hourDifference === 0) {
      const minuteDifference = nowDate.diff(pastDate, 'minutes');
      if (minuteDifference === 0) {
        return compact ? 'now' : 'just now!';
      }
      return compact ? `${minuteDifference}m` : `${minuteDifference} min`;
    }
    return compact ? `${hourDifference}h` : `${hourDifference} hr`;
  }
  if (dateDifference === 1) {
    return compact ? '1d' : 'Yesterday';
  }
  return compact ? `${dateDifference}d` : momentWithAustinTimezone(date).format('MMMM Do');
}

export function getDateRangeDuration({ fromDate, toDate }) {
  const fromMoment = momentWithAustinTimezone(fromDate);
  const toMoment = momentWithAustinTimezone(toDate);
  const diffInYears = toMoment.diff(fromMoment, 'years');
  if (diffInYears >= 1) {
    const diffInMonths = toMoment.diff(fromMoment, 'months') % 12;
    let dateString = pluralize('year', diffInYears, true);
    if (diffInMonths > 0) {
      dateString += ` ${pluralize('month', diffInMonths, true)}`;
    }
    return dateString;
  }
  return fromMoment.from(toMoment, true);
}

/**
 * Given a 'frontAutoText' table object, the function returns a formatted string
 * that corresponds to that stauts of the message.
 *
 * If the message has already been sent it will return: Sent
 * If the message is scheduled to be sent it will return: Scheduled for X/XX/XX hh:mm A
 * If the 'frontAutoText' is null it will return: Not Sent
 *
 * If the 'frontAutoText' object is null it signifies that an frontAutoText row was not
 * created and there for an autoText was never scheduled/sent.
 *
 * @param  {object}   frontAutoText                      frontAutoText table object.
 * @param  {string}   frontAutoText.id                   frontAutoText id.
 * @param  {string}   frontAutoText.queryId              frontAutoText queryId.
 * @param  {string}   frontAutoText.phone                renter phone number.
 * @param  {string}   frontAutoText.frontTextTaskId      type of task.
 * @param  {string}   frontAutoText.frontConversationId  conversationId.
 * @param  {string}   frontAutoText.lastFrontMessageId   id of the last front message.
 * @param  {string}   frontAutoText.messageReceived      message received from renter.
 * @param  {string}   frontAutoText.status               status of the messages.
 * @param  {date}     frontAutoText.lastStatusUpdateDate last date the status was update.
 * @param  {date}     frontAutoText.createdDate          date object was created.
 * @param  {date}     frontAutoText.updatedDate          date object was updated.
 * @param  {date}     frontAutoText.deadlineDate         date before sending next message.
 * @param  {object}   frontAutoText.data                 metadata.
 * @param  {string[]} frontAutoText.reminderDates        string of ISO dates when messages will be sent.
 * @param  {int}      frontAutoText.remindersSent        number of reminders that have been sent.
 *
 * @return {string}
 */
export function getFrontAutoTextStatus(frontAutoText) {
  let autoTextStr = null;

  if (!frontAutoText) {
    autoTextStr = 'Not Sent';
  } else if (
    frontAutoText &&
    frontAutoText.reminderDates &&
    frontAutoText.reminderDates.length > 0
  ) {
    const firstMessageSentDate = frontAutoText.reminderDates[0];
    const nowMoment = momentWithAustinTimezone().toISOString();
    if (momentWithAustinTimezone(firstMessageSentDate).isBefore(nowMoment)) {
      autoTextStr = 'Sent';
    } else {
      const timeStr = momentWithAustinTimezone(firstMessageSentDate).format('M/D/YY hh:mm A');
      autoTextStr = `Scheduled for ${timeStr.toLowerCase()}`;
    }
  }

  return autoTextStr;
}

/**
 * Given a 'renterActivity' array of objects, the function returns a formatted string
 * that corresponds to that stauts of the message, if there is an object of type 'assigned'.
 *
 * If the field 'autotextSentDate' exist in object 'customProps' then we know the date of when
 * the message was sent/ should be sent or was never sent. Otherwise we do not know the status
 * of the message and the method should return null.
 *
 * If the message has already been sent it will return:              Sent
 * If the message is scheduled to be sent it will return:            Scheduled for X/XX/XX hh:mm A
 * If the field 'autotextSentDate' exist and is null it will return: Not Sent
 * If the field 'autotextSentDate' does not exist it will return:    Null
 *
 *
 * @param  {array}    renterActivity                  array of renterActivity objects.
 * @param  {string}   renterActivity[x].id            frontAutoText id.
 * @param  {string}   renterActivity[x].renterUserId  frontAutoText queryId.
 * @param  {string}   renterActivity[x].listingId     renter phone number.
 * @param  {string}   renterActivity[x].activityType  type of task.
 * @param  {boolean}  renterActivity[x].seen          conversationId.
 * @param  {date}     renterActivity[x].createdDate   id of the last front message.
 * @param  {object}   renterActivity[x].customProps   message received from renter.
 *
 * @return {string}
 */
export function getRenterActivityAutoTextStatus(renterActivity) {
  typeCheck('renterActivity::Array', renterActivity);

  let autoTextStr = null;
  let assignedActivity = lodashFilter(renterActivity, (activity) => {
    return activity.activityType === LEAD_ACTIVITY_ASSIGNED;
  });

  assignedActivity = lodashSortBy(assignedActivity, ['createdDate']);
  assignedActivity = assignedActivity.reverse();

  if (!assignedActivity || assignedActivity.length < 1) {
    return autoTextStr;
  }

  assignedActivity = assignedActivity[0];

  if (!assignedActivity.customProps) {
    return autoTextStr;
  }
  if (
    assignedActivity &&
    assignedActivity.customProps &&
    assignedActivity.customProps.autotextSentDate &&
    assignedActivity.customProps.autotextSentDate !== null
  ) {
    const nowMoment = momentWithAustinTimezone().toISOString();
    if (
      momentWithAustinTimezone(assignedActivity.customProps.autotextSentDate).isBefore(nowMoment)
    ) {
      autoTextStr = 'Sent';
    } else {
      const timeStr = momentWithAustinTimezone(
        assignedActivity.customProps.autotextSentDate,
      ).format('M/D/YY hh:mm A');
      autoTextStr = `Scheduled for ${timeStr.toLowerCase()}`;
    }
  } else if (
    assignedActivity.customProps &&
    assignedActivity.customProps.autotextSentDate === null
  ) {
    autoTextStr = 'Not Sent';
  }

  return autoTextStr;
}

/**
 * Given a 'renterActivity' array of objects and a frontAutoText object the function
 * will return the proper autoText message status, with a prirority given to frontAutoText.
 * The function will return null if we have no trace of when the message was sent.
 *
 * If the message has already been sent it will return:        Sent
 * If the message is scheduled to be sent it will return:      Scheduled for X/XX/XX hh:mm A
 * If the message has not been sent we will return:            Not Sent
 * If we have no trace if the message was sent we will return: Null
 *
 * @param  {array}    renterActivity  array of renterActivity objects.
 * @param  {object}   frontAutoText   frontAutoText object.
 *
 * @return {string}
 */
export function getAutoTextStatus({ renterActivity, frontAutoText }) {
  let frontAutoTextStatus = null; //  If nothing exist will return 'Not Sent'
  let renterActivityStatus = null; // If nothing exist will return null

  if (frontAutoText) {
    frontAutoTextStatus = getFrontAutoTextStatus(frontAutoText);
  }

  if (renterActivity) {
    renterActivityStatus = getRenterActivityAutoTextStatus(renterActivity);
  }

  // If a front auto text status exist, and is not 'Not Sent'
  // Check to see if there is any renterActivity available
  // as a secondary fallback
  if (frontAutoTextStatus && renterActivityStatus === 'Not Sent') {
    // If renterActivity exist then it means the frontAutoText is correct
    // return the frontAutoTextStatus
    if (renterActivity) {
      return frontAutoTextStatus;
    }

    // If renterActivity does not exist
    // return renterActivityStatus which should return null
    return renterActivityStatus;
  }
  if (frontAutoTextStatus) {
    return frontAutoTextStatus;
  }
  if (renterActivityStatus) {
    return renterActivityStatus;
  }

  return frontAutoTextStatus;
}

export function formatListingLocation(listing) {
  return `${listing.locationLatitude},${listing.locationLongitude}`;
}

export function formatAgoDate(date, options = {}) {
  const pastDate = moment(date);
  const nowDate = moment();
  const numberOfDays = Math.round((nowDate.unix() - pastDate.unix()) / ONE_DAY_IN_SECONDS);
  const useDays = options.useDays || false;

  if (numberOfDays === 0) {
    const hourDifference = nowDate.diff(pastDate, 'hours');
    if (hourDifference === 0) {
      const minuteDifference = nowDate.diff(pastDate, 'minutes');
      if (minuteDifference === 0) {
        return 'just now!';
      }
      return `${minuteDifference}m ago`;
    }
    return `${hourDifference}h ago`;
  }

  if (useDays) {
    return `${numberOfDays}d ago`;
  }

  return formatAgoDateFromNumberOfDays(numberOfDays);
}

export function formatAgoDateFromNumberOfDays(numberOfDays) {
  if (numberOfDays >= 0 && numberOfDays <= 28) {
    return `${numberOfDays}d ago`;
  }
  if (numberOfDays > 28 && numberOfDays <= 45) {
    return '1 month ago';
  }
  if (numberOfDays > 45 && numberOfDays <= 319) {
    const months = Math.round(numberOfDays / 30);
    return `${months} months ago`;
  }
  if (numberOfDays > 319 && numberOfDays <= 547) {
    return '1 year ago';
  }

  const years = Math.round(numberOfDays / 365);
  return `${years} years`;
}

export function formatPercentage(percentage, decimals = true) {
  typeCheck('percentage::Number', percentage);
  if (!decimals) {
    percentage = parseFloat(percentage.toFixed(2));
  }
  return `${roundTwoDecimals(percentage * 100)}%`;
}

export function abbreviateNumber(num, digits) {
  const SI = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let i;
  for (i = SI.length - 1; i > 0; i -= 1) {
    if (num >= SI[i].value) {
      break;
    }
  }
  return (num / SI[i].value).toFixed(digits).replace(rx, '$1') + SI[i].symbol;
}

export function formatElapsedDisplay(fromMoment, toMoment) {
  const diff = toMoment.valueOf() - fromMoment.valueOf();
  if (diff > 1000) {
    return `${diff / 1000} seconds.`;
  }

  return `${diff} milliseconds.`;
}

export function formatPaymentAccount(paymentAccount) {
  if (!paymentAccount) {
    return '--';
  }

  if (paymentAccount.type === PAYMENT_ACCOUNT_BANK_ACCOUNT) {
    return `BANK ****${paymentAccount.bankAccountLast4}`;
  }

  if (paymentAccount.type === PAYMENT_ACCOUNT_CARD) {
    return `CARD ${paymentAccount.cardBrand.toUpperCase()} ****${paymentAccount.cardLast4}`;
  }

  return '--';
}

export function makeConversationTimeline({ userName, frontComments, frontMessages }) {
  const timeline = [
    ...frontMessages.map((frontMessage) => ({
      id: frontMessage.id,
      author: frontMessage.is_inbound
        ? userName
        : frontMessage.author
        ? `${frontMessage.author.first_name} ${frontMessage.author.last_name}`
        : 'Sunroom Customer Number',
      phone: frontMessage.recipients.find(({ role }) => role === 'from').handle,
      createdAtDate: moment.unix(frontMessage.created_at).format('MMM D, h:mm A'),
      text: frontMessage.text,
      date: frontMessage.created_at * 1000,
      type: 'message',
      isMessage: true,
      isInbound: frontMessage.is_inbound,
      role: frontMessage.is_inbound ? 'user' : 'system',
    })),
    ...frontComments.map((frontComment) => ({
      id: frontComment.id,
      author: frontComment.author
        ? `${frontComment.author.first_name} ${frontComment.author.last_name}`
        : 'Sunroom App',
      createdAtDate: moment.unix(frontComment.posted_at).format('MMM D, h:mm A'),
      text: frontComment.body,
      type: 'comment',
      date: frontComment.posted_at * 1000,
      isComment: true,
      role: 'system',
    })),
  ];

  timeline.sort((a, b) => b.date - a.date).reverse();

  if (timeline[1] && timeline[1].text.includes('Lead information:')) {
    const tmp = timeline[0];
    timeline[0] = timeline[1];
    timeline[1] = tmp;
  }

  return timeline;
}

export function formatName(name, style) {
  switch (style) {
    case 'shortAbbr': {
      const nameParts = name.split(' ');
      return `${capitalize(nameParts[0] || '')} ${
        nameParts[1] ? `${capitalize(nameParts[1])[0]}.` : ''
      }`.trim();
    }
    default: {
      return capitalize(name);
    }
  }
}
